import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { EXPECTED_WORD, expectedBubbleSentence } from '@/lib/expected-noyau-words';
import { NOYAU_DEEP_CLASS } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import { type ExpectedNoyauMark, type NoyauCode, noyauOf } from '@matter_extractor/shared';
import { type Noyau, NoyauBadge, Tooltip } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';

/**
 * The RETAINED noyau of a line of Sessions (noyau.md §9.2, revue du
 * 2026-09-25): a pill in the noyau's official colour, written by its TRIPLET
 * to hold in the line, its name in the bubble.
 *
 * ## A link to the session, and not a mute span
 *
 * « La ligne ENTIÈRE ouvre la session » (`sessions-table.tsx`): the row's link
 * lays a veil over the whole line, and a pill under it would never see the
 * pointer that opens its bubble. So the pill stands ABOVE the veil
 * (`relative z-10`) and is itself the same link - a click on it still opens the
 * session. It leaves the tab order and the accessibility tree to the row's own
 * link, whose line carries the name in words (`sr-only`), so a keyboard and a
 * screen reader meet the session once and the noyau by its name.
 *
 * ## The trial's pill (#170)
 *
 * On a line that retained no noyau, the column shows the principal of the last
 * trial run from the session (§9.4): the SAME pill, filled in the noyau's
 * colour (revue du 2026-09-29), told apart only by « lu à blanc » in its bubble
 * and for a screen reader - a reading of the instrument, never a retained noyau.
 *
 * The pill is the package's `NoyauBadge`: the colour and the ink are the
 * charte's, so the triplet reads the same here as on every PMS screen. Only the
 * code-to-colour pairing lives here, the one of the table in `shared`.
 */

/** The charte colour of each noyau, as the table of `shared` pairs them. */
const CHARTE_NOYAU: Readonly<Record<NoyauCode, Noyau>> = {
  SGP: 'bleu-nuit',
  SPG: 'bleu-ciel',
  GSP: 'rose',
  GPS: 'rouge',
  PGS: 'jaune',
  PSG: 'vert',
};

export interface NoyauPillProps {
  readonly code: NoyauCode;
  readonly sessionId: string;
  /** The principal of a trial rather than the retained noyau: worded so, drawn alike. */
  readonly trial?: boolean;
}

export function NoyauPill({ code, sessionId, trial = false }: NoyauPillProps) {
  const noyau = noyauOf(code);
  return (
    <>
      <Tooltip content={trial ? `${noyau.name} (lu à blanc)` : noyau.name}>
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId }}
          tabIndex={-1}
          aria-hidden="true"
          data-testid={SESSIONS_TEST_IDS.rowNoyau}
          data-noyau={code}
          data-source={trial ? 'trial' : undefined}
          className="relative z-10 inline-flex rounded-full"
        >
          <NoyauBadge noyau={CHARTE_NOYAU[code]}>{code}</NoyauBadge>
        </Link>
      </Tooltip>
      <span className="sr-only">
        {trial ? `Noyau lu à blanc : ${noyau.name}` : `Noyau retenu : ${noyau.name}`}
      </span>
    </>
  );
}

export interface ExpectedNoyauPillProps {
  /** The mark of the replay, whose expected noyau is a triplet (`expectedTripletMark`). */
  readonly mark: ExpectedNoyauMark & { readonly expected: NoyauCode };
  readonly sessionId: string;
  /** The NPC the replay played, named in the bubble: the row's column Lien already names it. */
  readonly npcName: string | null | undefined;
}

/**
 * The EXPECTED noyau of the NPC a replay played (npc.md §26.6, H37; issue #216, lot M): a small
 * pill outlined in DASHES - « attendu SGP », the word and the triplet inside, in the `-deep`
 * shade of the expected noyau - beside the pill of the retained one. The dashes tell it from the
 * retained pill's fill: it is a thing the scenario says, never a thing the machine found.
 *
 * It is told in words, never by a colour alone: « attendu » and the triplet are IN the pill, and
 * its bubble says the version, the found noyau and the verdict (`expectedBubbleSentence`).
 *
 * Like the retained pill it stands ABOVE the veil of the row (`relative z-10`) and is itself the
 * same link, out of the tab order and the accessibility tree, the sentence being read once by a
 * screen reader in the `sr-only` span.
 *
 * Drawn here and not by the package: its badges have no dashed outline, so the pill keeps the
 * markup of #216 on the charte's tokens until the package offers one.
 */
export function ExpectedNoyauPill({ mark, sessionId, npcName }: ExpectedNoyauPillProps) {
  const sentence = expectedBubbleSentence(mark, npcName);
  return (
    <>
      <Tooltip content={sentence}>
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId }}
          tabIndex={-1}
          aria-hidden="true"
          data-testid={SESSIONS_TEST_IDS.rowExpected}
          data-noyau={mark.expected}
          data-verdict={mark.comparison?.verdict}
          data-provenance={mark.provenance.kind}
          className={cn(
            'relative z-10 inline-flex h-5 items-center gap-1 rounded-full border-2 border-dashed px-1.5 font-bold text-micro leading-none',
            NOYAU_DEEP_CLASS[mark.expected],
          )}
        >
          <span className="font-normal">{EXPECTED_WORD}</span>
          {mark.expected}
        </Link>
      </Tooltip>
      <span className="sr-only">{sentence}</span>
    </>
  );
}
